import 'leaflet/dist/leaflet.css'

import React, { useEffect, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L, { LatLngExpression } from 'leaflet'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'
import { useNavigate } from 'react-router-dom'
import { Roles } from '@/services/repository/users'
import { defaultMapCenter } from '@/env/config'
import { formatDate } from '@/common/time'

import { useAppContext } from '@/context/app'
import { MachineCreateWizard } from '@/popups/machines/wizard/create/Main'
import { useModal } from '@/components/modal/Modal'
import { MapResponse, MapRow, mergeMapResponses, vehicleIconSize, vehicleObject } from '@/components/Map/Map'
import { Spinner } from '@/components/spinner'
import { Machine } from '@/services/model/machine'

const PopupBody = ({ row, onSelect }: {
  row: MapRow
  onSelect: (machine: Machine) => void
}) => {
  const { services, session, translates } = useAppContext()
  const navigate = useNavigate()
  const { openModal } = useModal()

  if (!session) {
    return <Spinner />
  }

  return <div className='map-popup-wrap'>
    {row.machine?.type?.manufacturer ? <p>
      {row.machine.type.manufacturer} -
      {row.machine.type.category ? session.translates.for(`machine.types.${row.machine.type.category}`) : null} -
      {row.machine.type.model} -
      {row.machine.internalNumber}
    </p> : null}
    {row.imei ? <p>
      IMEI: {row.imei}
    </p> : null}

    <p className='main-btn' onClick={() => onSelect({
      id: row.machine.id
    })}>{session.translates.for('map.add_btn')}</p>
  </div>
}

export const ActivateMapComponent = ({ fetchUrl, onSelect }: {
  fetchUrl: string
  onSelect: (machine: Machine) => void
}) => {
  const { services, session, translates } = useAppContext()
  const [map, setMap] = useState<MapResponse | undefined>(undefined)
  const [myposition, setMyPosition] = useState<[number, number] | undefined>(undefined)
  const navigate = useNavigate()
  const { openModal } = useModal()

  async function refreshMap(fromDate?: Date, oldResponse?: MapResponse): Promise<MapResponse> {
    return services.session.fetchWithToken(`${fetchUrl}?fromDate=${fromDate ? fromDate.toISOString() : ''}`).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        console.error(erros)
        return Promise.reject<MapResponse>()
      }
      const newResponse: MapResponse = await r.json()
      newResponse.rows = newResponse.rows || null
      if (oldResponse === undefined) {
        return newResponse
      }
      return mergeMapResponses(oldResponse, newResponse)
    })
  }

  useEffect(() => {
    // Function to handle geolocation success
    const handleSuccess = (location: GeolocationPosition) => {
      const { latitude, longitude } = location.coords
      setMyPosition([latitude, longitude])
    }

    // Function to handle geolocation error
    const handleError = (error: GeolocationPositionError) => {
      console.error('Error obtaining location:', error)
      // Fallback to a default position if geolocation fails
      setMyPosition(undefined)
    }

    // Check if geolocation is available
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError)
    } else {
      console.error('Geolocation is not supported by this browser.')
      // Fallback to a default position if geolocation is not supported
      setMyPosition(undefined)
    }
  }, [])

  useEffect(() => {
    refreshMap().then(setMap)
  }, []);

  useEffect(() => {
    if (map === undefined) {
      return
    }
    const clock = async () => {
      if (map === undefined) {
        return
      }
      await refreshMap(map?.time ? new Date(map?.time) : undefined, map).then(setMap)
      handle = setTimeout(clock, 2000)
    }
    let handle: NodeJS.Timeout = setTimeout(clock, 2000)
    return () => {
      clearTimeout(handle)
    }
  }, [map])

  if (map === undefined) {
    return <Spinner />
  }

  function findLatestRow(response: MapResponse | undefined): MapRow | undefined {
    if (!response?.rows || response.rows.length === 0) {
      return undefined;
    }

    return response.rows.reduce((latest, current) => {
      return new Date(current.time) > new Date(latest.time) ? current : latest;
    });
  }

  const firstRow = findLatestRow(map);

  const center: LatLngExpression = (firstRow && {
    lat: firstRow.lat,
    lng: firstRow.lng
  }) || myposition || defaultMapCenter

  return (
    <MapContainer attributionControl={false} center={center} zoom={13} style={{ height: '100%', width: '100%' }}>
      <TileLayer
        attribution='evoltag.pl'
        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
      />
      {map.rows?.map((row) => (
        <Marker
          key={row.imei}
          position={row}
          icon={L.divIcon(vehicleObject(row))} >
          <Popup>
            <PopupBody row={row} onSelect={onSelect} />
          </Popup>
        </Marker>
      ))}

      {myposition ? <Marker
        key='my-location'
        position={myposition}
        icon={L.divIcon({
          className: 'car-icon',
          html: `<img src="/public/img/gps/i_am_here.png" style="width: 32px;" />`,
          iconSize: [32, 32], // Adjust size as needed
          iconAnchor: [16, 16], // Anchor point
        })}></Marker> : null}
    </MapContainer>
  )
}
